// src/features/settings/components/registration-codes-panel/RegistrationCodeEditDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';

export default function RegistrationCodeEditDialog({ open, row, loading, onClose, onSubmit }) {
  const { t } = useTranslation(['settings', 'common']);

  const [values, setValues] = useState({
    title: '',
    description: '',
    is_wishable: false,
    is_allowed_without_end_date: false,
    required_seniority_months: 0,
    max_per_period: '',
    requires_documentation: false,
  });

  useEffect(() => {
    if (row) {
      setValues({
        title: row.title ?? '',
        description: row.description ?? '',
        is_wishable: row.is_wishable ?? false,
        is_allowed_without_end_date: row.is_allowed_without_end_date ?? false,
        required_seniority_months: row.required_seniority_months ?? 0,
        max_per_period: row.max_per_period ?? '',
        requires_documentation: row.requires_documentation ?? false,
      });
    }
  }, [row]);

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const payload = {
      title: values.title || undefined,
      description: values.description || undefined,
      is_wishable: values.is_wishable,
      is_allowed_without_end_date: values.is_allowed_without_end_date,
      required_seniority_months: values.required_seniority_months,
      max_per_period: values.max_per_period === '' ? null : Number(values.max_per_period),
      requires_documentation: values.requires_documentation,
    };
    onSubmit(payload);
  };

  if (!row) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('settings:registration_codes.form.edit_title')}
        <Typography variant="caption" display="block" color="text.secondary">
          {row.code} — {row.title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label={t('settings:registration_codes.labels.title')}
            value={values.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            label={t('settings:registration_codes.labels.description')}
            value={values.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 1000 }}
          />

          <Box display="flex" gap={2}>
            <TextField
              label={t('settings:registration_codes.labels.required_seniority_months')}
              type="number"
              value={values.required_seniority_months}
              onChange={(e) => handleChange('required_seniority_months', Number(e.target.value))}
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('settings:registration_codes.labels.max_per_period')}
              type="number"
              value={values.max_per_period}
              onChange={(e) => handleChange('max_per_period', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1}>
            {[
              ['is_wishable', 'is_wishable'],
              ['is_allowed_without_end_date', 'is_allowed_without_end_date'],
              ['requires_documentation', 'requires_documentation'],
            ].map(([field, labelKey]) => (
              <FormControlLabel
                key={field}
                control={
                  <Switch
                    checked={Boolean(values[field])}
                    onChange={(e) => handleChange(field, e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t(`settings:registration_codes.labels.${labelKey}`)}
                  </Typography>
                }
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onClose}
          disabled={loading}
        >
          {t('common:buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading || !values.title.trim()}
        >
          {t('common:buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
